import { useCallback, useEffect, useRef, useState } from "react";
import { FormInstance } from "antd";
import { Meta, SetterConfig, UpdatePayload } from "../types";
import jsonLogic from "json-logic-js";
import { EVENT_TYPES } from "../constants";

export const useFormBinding = (
  meta: Meta,
  form: FormInstance,
  handleEffects: (
    changedValues: Record<string, any>,
    allValues: Record<string, any>
  ) => void,
  onChange: (payload: UpdatePayload) => void
) => {
  // 使用 useRef 跟踪初始化状态
  const isInitializedRef = useRef(false);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  // 获取 setter 配置
  const getSetterConfig = useCallback(
    (field: string): SetterConfig | undefined => {
      return meta.setters.props.find(
        (config) => config.name === field.split("/").pop()
      );
    },
    [meta.setters.props]
  );

  // 获取选项配置
  const getOptions = useCallback(
    (config: SetterConfig | undefined, values: Record<string, any>) => {
      if (!config?.options || Array.isArray(config.options)) {
        return config?.options;
      }

      const { mapping } = config.options;
      const dependencyValue = values[mapping.dependency];
      const sourceData = meta.datasource?.[mapping.source]?.[dependencyValue];

      if (!sourceData || !mapping.transform) {
        return sourceData;
      }

      return jsonLogic.apply(mapping.transform.map[1], {
        data: sourceData,
      });
    },
    [meta.datasource]
  );

  // 处理表单值变化
  const handleValuesChange = useCallback(
    (changedValues: Record<string, any>, allValues: Record<string, any>) => {
    //    // 更新表单值
    // const fieldName = Object.keys(changedValues)[0];
    // const newValue = changedValues[fieldName];

    //  // 设置表单字段值
    //  form.setFieldValue(fieldName, newValue);
    
      // 更新字段可见性
      updateVisibility(allValues);
      // 处理联动效果
      handleEffects(changedValues, allValues);
       // 触发外部 onChange
      onChange?.({
      type: EVENT_TYPES.UPDATE_VALUE,
      data: allValues
    });
  
    },
    [form, handleEffects, onChange]
  );

  // 更新字段可见性
  const updateVisibility = useCallback(
    (values: Record<string, any>) => {
      const newVisibleFields = new Set<string>();

      meta.setters.props.forEach((setter) => {
        const { name, visible } = setter;
        if (!visible || jsonLogic.apply(visible, values)) {
          newVisibleFields.add(name);
        }
      });

      setVisibleFields(newVisibleFields);
    },
    [meta.setters.props]
  );

  // 初始化 effect
  useEffect(() => {
    if (!isInitializedRef.current) {
      // 设置初始表单值
      form.setFieldsValue(meta.defaultValues.props);
      // 更新初始可见性
      updateVisibility(meta.defaultValues.props);
      isInitializedRef.current = true;
    }
  }, [meta, form, updateVisibility]);

  return {
    visibleFields,
    getSetterConfig,
    getOptions,
    handleValuesChange,
    updateVisibility,
  };
};
